/**
 * SectionHeader — optional eyebrow / heading / intro band shared by the
 * grid + stat kinds (feature_card_grid, category_grid, topic_grid,
 * stat_band).
 *
 * Rendered INSIDE each section's own container so it aligns to the grid's
 * full width — unlike a standalone rich_text section, whose `prose` variant
 * clamps to the narrower `max-w-prose`. Renders nothing when all three
 * fields are absent, so existing header-less grids are unchanged.
 *
 * Headings follow the dealer's heading font + optional weight override
 * (`--brand-font-heading-weight`), matching ProductFinderSection /
 * HeroSections. `data-field` attributes opt each line into the storefront
 * inline-edit overlay.
 */
import type { CSSProperties } from 'react'
import { splitBoldSegments } from '@/lib/inlineBold'
import { eyebrowSurface, sectionEyebrowStyle, sectionHeadlineStyle } from '@sifly/shared-ui/section-typography'

const HEADING_STYLE: CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontWeight: 'var(--brand-font-heading-weight, 600)' as CSSProperties['fontWeight'],
}

// Intro copy supports a `**bold**` lead sentence; the bold run steps up to
// full-contrast text so it reads as the statement, the rest as support.
// Exported for sections that place their intro outside a SectionHeader
// (media_checklist with its heading on the photo), so the two never drift.
export function renderIntroText(text: string, headingColor: string) {
  return splitBoldSegments(text).map((seg, i) =>
    seg.bold ? (
      <strong key={i} style={{ fontWeight: 600, color: headingColor }}>
        {seg.text}
      </strong>
    ) : (
      seg.text
    ),
  )
}

export type SectionHeaderData = {
  eyebrow?: string
  heading?: string
  intro?: string
}

// On a dark band (a section painted with surface_tone inverse/accent) the
// header sits ON the band, so its default dark text would render
// dark-on-dark. `onDark` flips heading/eyebrow/intro to light tones.
// Defaults to false so every existing caller is unchanged.
//
// `align` controls horizontal layout. 'center' (default) keeps today's
// `max-w-2xl` clamp so the header reads as a contained band — byte-identical
// to every existing caller. 'start' drops the clamp so the header fills and
// left-aligns within a narrower side column (intro-beside-cards layout).
//
// `layout` controls the header's own composition. 'stack' (default) is the
// eyebrow/heading/intro stack. 'split' is the full-width statement band:
// eyebrow + heading in the left column, intro in the right column, hairline
// divider underneath — the feature_card_grid `header_placement: 'split'`
// opener.
//
// Both layouts render the headline at the SAME size (`SECTION_HEADLINE_SIZE`).
// There used to be a `scale` prop here — `--text-2xl` by default, stepping up
// to the display ramp only for booking_widget's split layout — which is how
// one page ended up with 24px and 64px section headings side by side. Size is
// no longer a per-caller decision; `layout` picks the composition, not the
// scale.
export function SectionHeader({
  data,
  onDark = false,
  align = 'center',
  layout = 'stack',
}: {
  data: SectionHeaderData
  onDark?: boolean
  align?: 'center' | 'start'
  layout?: 'stack' | 'split'
}) {
  if (!data.eyebrow && !data.heading && !data.intro) return null
  const headingColor = onDark ? 'var(--color-on-surface-inverse, #f8fafc)' : 'var(--color-text-primary)'
  const mutedColor = onDark
    ? 'color-mix(in oklch, var(--color-on-surface-inverse, #f8fafc) 75%, transparent)'
    : 'var(--color-text-secondary)'

  // One eyebrow treatment for both layouts — a spoken line over the headline,
  // not a spaced-out kicker. The stack layout used to run its own tracked,
  // muted-grey variant; that split is the drift `sectionTypography` exists to
  // close, so the `layout` branch is deliberately gone from here.
  const eyebrow = data.eyebrow ? (
    <p className="mb-2" style={sectionEyebrowStyle(eyebrowSurface(onDark))} data-field="data.eyebrow">
      {data.eyebrow}
    </p>
  ) : null

  const renderIntro = (text: string) => renderIntroText(text, headingColor)

  if (layout === 'split') {
    // Statement band: eyebrow + display heading left, intro right, divider
    // under the whole band. The divider follows band darkness so it stays a
    // quiet hairline on both light and dark surfaces.
    const divider = onDark
      ? '1px solid color-mix(in oklch, var(--color-on-surface-inverse, #f8fafc) 30%, transparent)'
      : '1px solid var(--color-border)'
    return (
      <div className="mb-10 md:mb-14 text-left" style={{ borderBottom: divider }}>
        <div className="grid gap-6 md:gap-12 md:grid-cols-2 items-start pb-8 md:pb-10">
          <div>
            {eyebrow}
            {data.heading ? (
              <h2
                style={{
                  ...HEADING_STYLE,
                  fontFamily: 'var(--section-headline-family, var(--font-heading))',
                  ...sectionHeadlineStyle(headingColor),
                }}
                data-field="data.heading"
              >
                {data.heading}
              </h2>
            ) : null}
          </div>
          {data.intro ? (
            <p
              className="md:pt-1"
              style={{ fontSize: 'var(--text-lg)', lineHeight: 'var(--leading-lg)', color: mutedColor }}
              data-field="data.intro"
            >
              {renderIntro(data.intro)}
            </p>
          ) : null}
        </div>
      </div>
    )
  }

  const alignClasses = align === 'start' ? 'text-left' : 'max-w-2xl'
  return (
    <div className={`mb-8 ${alignClasses}`}>
      {eyebrow}
      {data.heading ? (
        <h2
          className="font-semibold"
          style={{
            ...HEADING_STYLE,
            fontFamily: 'var(--section-headline-family, var(--font-heading))',
            ...sectionHeadlineStyle(headingColor),
          }}
          data-field="data.heading"
        >
          {data.heading}
        </h2>
      ) : null}
      {data.intro ? (
        <p
          className="mt-2"
          style={{
            // The lede steps up with the headline — a body-size intro under a
            // display headline reads as a caption, not a paragraph.
            fontSize: 'var(--text-lg)',
            lineHeight: 'var(--leading-lg)',
            color: mutedColor,
          }}
          data-field="data.intro"
        >
          {renderIntro(data.intro)}
        </p>
      ) : null}
    </div>
  )
}
